import List from '@pelckmans/elementis/components/list';
import Section from '@pelckmans/elementis/components/section';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyAssignment } from '../../../../../api/assignment';
import { Module } from '../../../../../api/module';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import MyAssignmentsContext from '../../../../../contexts/MyAssignmentsContext';
import useLicenses from '../../../../../hooks/useLicenses';
import { AssignmentDeadlinePill } from '../../../../../shared-components/AssignmentDeadlinePill';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { MaterialIcon } from '../../../components/MaterialIcon';
import MedialinksContext from '../../_contexts/MedialinksContext';
import TableOfContentsContext from '../../_contexts/TableOfContentsContext';
import useAssignmentHandling from '../../hooks/assignments/useAssignmentHandling';
import MaterialPills from '../MaterialPills';
import ScorableProgress from '../ScorableProgress';
import PinnedMaterialButton from '../pinned-material/PinnedMaterialButton';

export default function AssignmentSection({
  assignments: assignmentsByModule,
  module,
}: {
  assignments: MyAssignment[];
  module: Module;
}) {
  const { t } = useTranslation();
  const { refreshAssignments } = useContext(MyAssignmentsContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { isModuleLicensed } = useLicenses();
  const isUnlicensed = !isModuleLicensed(module);
  const { medialinks } = useContext(MedialinksContext);

  const { startAssignment, resumeAssignment } = useAssignmentHandling({
    onAssignmentClosed: refreshAssignments,
    subLocationId: module.isExerciseModule
      ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_ASSIGNMENT_MATERIAL
      : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MODULE_ASSIGNMENT_MATERIAL,
    assignmentModule: module,
  });

  const getPills = (assignment: MyAssignment) => {
    const medialink = medialinks.find(x => x.id === assignment.materialId);
    const isAdaptive = medialink?.assessmentQExercise?.isAdaptive;
    const isOnTheFly = medialink?.assessmentQExercise?.isOnTheFly;

    return (
      <>
        <MaterialPills key="material-pills" isAdaptive={isAdaptive} isOnTheFly={isOnTheFly} />
        <AssignmentDeadlinePill key="deadline-pill" deadline={assignment.to} />
      </>
    );
  };

  const handleOpenAssignment = useCallback(
    async (assignment: MyAssignment) => {
      if (!selectedNode) return;

      if (Boolean(assignment.score.achievedAt)) {
        await resumeAssignment(assignment);
      } else {
        await startAssignment(assignment);
      }
    },
    [selectedNode, resumeAssignment, startAssignment],
  );

  if (assignmentsByModule.length === 0) return null;

  return (
    <Section title={t(`PAGES.TOC_LEVEL.SECTIONS.STUDENT.ASSIGNMENTS.TITLE`)}>
      {assignmentsByModule.length > 0 && (
        <List>
          {assignmentsByModule.map(assignment => {
            const isMediaLinkUnlicensed = assignment.materialType === 'medialink' && isUnlicensed;
            return (
              <List.Item
                onClick={() => handleOpenAssignment(assignment)}
                key={assignment.id}
                title={assignment.name}
                icon={<MaterialIcon materialType={assignment.materialType} contentType={assignment.contentType} />}
                pillGroupProps={buildGroupProps(getPills(assignment))}
                contentArea={<ScorableProgress unachievable={assignment.unachievable} score={assignment.score} />}
                actionArea={
                  <PinnedMaterialButton
                    type={assignment.materialType}
                    materialId={assignment.materialId}
                    disabled={isMediaLinkUnlicensed}
                  />
                }
                as="button"
                disabled={isMediaLinkUnlicensed}
              />
            );
          })}
        </List>
      )}
    </Section>
  );
}
